import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENCustomProductContextAvailability from "@tCentro/Components/CENCustomProductContextAvailability";
import NISCustomProductContextAvailability from "@tCentro/Components/NISCustomProductContextAvailability";
import { NISProductAvailabilityStyles } from "@tCentro/Components/NISProductAvailability";
import { ProductAvailabilityStyles } from "@tCentro/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@tCentro/Overrides/Components/ProductContextAvailability";
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import TextField from "@insite/mobius/TextField";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import Typography from "@insite/mobius/Typography";
import siteMessage from "@insite/client-framework/SiteMessage";
import submitFasterFormHandler from "@tCentro/Handlers/NISPDPSubmitFasterFormHandler";
import submitDeliveryDateFormHandler from "@tCentro/Handlers/NISPDPDeliveryDateFormHandler";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    configurationCompleted: state.pages.productDetails.configurationCompleted,
});

const mapDispatchToProps = {
    submitFasterFormHandler,
    submitDeliveryDateFormHandler,
};

export interface NISProductDetailsAvailabilityStyles {
    availability?: NISProductAvailabilityStyles;
    zoneStyles?: InjectableCss;
    modalStyles?: ModalPresentationProps;
    fieldset: InjectableCss;
    containerHeading?: InjectableCss;
    subHeading?: InjectableCss;
    resident?: InjectableCss;
    residentLineOne?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const nisAvailabilityStyles: NISProductDetailsAvailabilityStyles = {
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    submitButton: {
        css: css`
            border-radius: 24px;
            padding: 12px 24px;
            margin-top: 20px;
            span {
                font-weight: 600 !important;
                font-size: 18px !important;
                line-height: 24px !important;
                font-family: Poppins !important;
            }
        `,
    },
    containerHeading: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 14px;
            padding-top: 20px;

            label {
                font-family: Poppins;
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
                color: #262525;
                padding-left: 20px;
                align-items: center;
                display: flex;
            }

            input {
                padding: 0px 10px 0px 24px !important;
                background-color: #f2f2f2 !important;
                border-radius: 24px !important;
                height: 49px !important;
                border: none !important;
                margin-top: -4px;
            }

            input[aria-invalid="true"] {
                border: 2px solid #ed0000 !important;
            }

            [data-test-selector*="error"] {
                color: #ed0000 !important;
                padding-left: 20px !important;
                margin-bottom: 5px !important;
                font-weight: 700 !important;
                font-family: Poppins;
                font-size: 14px;
                line-height: 20px;
            }

            @media (min-width: 768px) {
                & > div:first-child {
                    & > div:first-child {
                        max-width: 20%;
                    }
                    & > div:nth-child(2) {
                        max-width: 40%;
                        margin-left: 6px;
                    }
                }
            }
        `,
    },

    subHeading: {
        css: css`
            margin-top: 24px;
            font-family: Poppins;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
            color: #262525;
        `,
    },
    resident: {
        css: css`
            margin-top: 24px;
            padding-bottom: 20px;
        `,
    },
    residentLineOne: {
        css: css`
            font-size: 14px;
            font-weight: 700;
            line-height: 24px;
            color: #262525;
        `,
    },
    fieldset: {
        css: css`
            border: none !important;
            margin: 0px !important;
            padding: 0px !important;
        `,
    },
    availability: {
        infoLinkStyles: {
            css: css`
                font-size: ${TYPOGRAPHY.size.tiny};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: 16px;
                margin-left: 4px;
                color: ${getColor("text.main")};
            `,
        },
        infoIconStyles: {
            size: 12,
            color: `${getColor("text.main")}`,
            css: css`
                svg {
                    > path {
                        fill: ${getColor("text.main")} !important;
                    }
                }
            `,
        },
        container: {
            gap: 0,
            css: css`
                flex-grow: 0;
            `,
        },
    },
    zoneStyles: {
        css: css`
            margin-top: 24px;
        `,
    },
    modalStyles: {
        cssOverrides: {
            modalTitle: css`
                border-bottom: none !important;
                padding: 0;
                h4 {
                    font-family: Poppins;
                    font-size: 21px;
                    font-weight: 700;
                    line-height: 40px;
                }
                button[data-test-selector^="modalCloseButton"] {
                    position: absolute;
                    right: 16px;
                    top: 16px;

                    span {
                        color: #262525 !important;
                    }
                }
            `,
            modalContent: css`
                padding: 0;
            `,
            modalContainer: css`
                > div {
                    padding: 40px !important;
                }
            `,
        },
    },
};

const styles = nisAvailabilityStyles;

const NISProductDetailsAvailability = ({
    product,
    configurationCompleted,
    id,
    productInfo,
    submitDeliveryDateFormHandler,
    submitFasterFormHandler,
}: Props) => {
    const [getFasterModalIsOpen, setGetFasterModalIsOpen] = React.useState(false);
    const [zipCode, setZipCode] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [emailError, setEmailError] = React.useState("");
    const [qty, setQty] = React.useState<string>("");
    const [onSuccess, setOnSuccess] = React.useState(true);

    const zipCodeRequiredFieldMessage = siteMessage("inquiryLineCard_zipCodeRequiredFieldMessage");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const [qtyError, setQtyError] = React.useState("");
    const [zipCodeError, setZipCodeError] = React.useState("");
    const emailRegexp = /^[\w.+'-]+@[\w.-]+\.(?!c0m$|cim$|con$|c0n$|usa$)[A-Za-z]{2,}$/i;
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const [formErrors, setFormErrors] = React.useState<any>("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [resultError, setResultError] = React.useState("");
    const inventoryInfo = productInfo?.inventory?.inventoryAvailabilityDtos;
    const availability = inventoryInfo && inventoryInfo[0]?.availability;
    const isCustomAvailabilityMsg = product?.properties?.isCustomAvailabilityMsg;

    if (!product) {
        return null;
    }

    const closeModalHandler = () => {
        setGetFasterModalIsOpen(false);
        setZipCode("");
        setEmail("");
        setQty("");
        setOnSuccess(true);
    };
    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(typeof errorMessage === "string" ? errorMessage : "");
        return !errorMessage;
    };
    const validateQty = (qty: string) => {
        if (!qty || qty.trim() === "") {
            setQtyError("Quantity is required");
            return false;
        }

        if (!/^\d+$/.test(qty)) {
            setQtyError("Enter a valid quantity");
            return false;
        }

        setQtyError("");
        return true;
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };
    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value === "") {
            setQty("");
            setQtyError("Quantity is required");
            return;
        }

        if (/^\d+$/.test(value)) {
            setQty(value);
            setQtyError("");
        }
    };

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };
    const validateZipCode = (zipCode: string) => {
        const errorMessage = isNonEmptyString(String(zipCode));
        setZipCodeError(!errorMessage ? String(zipCodeRequiredFieldMessage) : "");
        return errorMessage;
    };
    const zipCodeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setZipCode(e.target.value);
        validateZipCode(e.target.value);
    };
    const validateForm = () => {
        const qtyValid = validateQty(qty);
        const zipCodeValid = validateZipCode(zipCode);
        const emailValid = validateEmail(email);

        return qtyValid && zipCodeValid && emailValid;
    };
    if (typeof window === "undefined") {
        return null;
    }

    function getDataLayer() {
        return (window as any).dataLayer;
    }
    const dataLayer = getDataLayer();

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateForm()) {
            setFormErrors(true);
            return;
        }
        setIsLoading(true);
        if (availability?.messageType === 2 && isCustomAvailabilityMsg === "false") {
            submitDeliveryDateFormHandler({
                qty: Number(qty),
                zipCode,
                email,
                productNumber: product.manufacturerItem,
                productTitle: product.productTitle,
                productUrl: product.canonicalUrl,

                onSuccess: response => {
                    setIsLoading(false);
                    setOnSuccess(false);
                    if (dataLayer) {
                        dataLayer.push({
                            event: "getadeliverydateFormSubmit",
                            formID: "get_a_delivery_date_for_my_location",
                        });
                    }
                },

                onError: () => {
                    setIsLoading(false);
                },
                onComplete: () => {},
            });
        }
        if (availability?.messageType === 2 && isCustomAvailabilityMsg === "true") {
            submitFasterFormHandler({
                qty: Number(qty),
                zipCode,
                email,
                productNumber: product.manufacturerItem,
                productTitle: product.productTitle,
                productUrl: product.canonicalUrl,

                onSuccess: response => {
                    setIsLoading(false);
                    setOnSuccess(false);
                    if (dataLayer) {
                        dataLayer.push({
                            event: "canIgetThisFasterFormSubmit",
                            formID: "can_i_get_this_faster",
                        });
                    }
                },

                onError: () => {
                    setIsLoading(false);
                },

                onComplete: () => {},
            });
        }
    };

    React.useEffect(() => {
        if (getFasterModalIsOpen) {
            const observer = new MutationObserver(() => {
                const manufacturerInput = document.querySelector<HTMLInputElement>(
                    "input[name='manufacturer_s_part_number']",
                );
                const categoryInput = document.querySelector<HTMLInputElement>("input[name='product_category']");
                const vendorInput = document.querySelector<HTMLInputElement>("input[name='product_vendor']");
                if (manufacturerInput) {
                    manufacturerInput.value = product?.manufacturerItem ?? "Part Number";
                }
                if (categoryInput) {
                    categoryInput.value = product?.properties?.parentCategoryName ?? "Category";
                }
                if (vendorInput) {
                    vendorInput.value = product?.brand?.name ?? "Vendor";
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            return () => observer.disconnect(); // Cleanup when modal closes
        }
    }, [getFasterModalIsOpen]);
    const StyledForm = getStyledWrapper("form");
    const StyledFieldSet = getStyledWrapper("fieldset");

    return (
        <>
            <NISCustomProductContextAvailability
                isProductDetailsPage={true}
                configurationCompleted={configurationCompleted}
                extendedStyles={styles.availability}
                getFasterModalIsOpen={getFasterModalIsOpen}
                setGetFasterModalIsOpen={setGetFasterModalIsOpen}
            />
            <Modal
                headline={translate("Product available for purchase")}
                isOpen={getFasterModalIsOpen}
                handleClose={closeModalHandler}
                {...styles.modalStyles}
            >
                {onSuccess ? (
                    isLoading ? (
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>
                    ) : (
                        <StyledWrapper {...styles.zoneStyles}>
                            <StyledForm
                                id="availabilityForm"
                                noValidate={true}
                                onSubmit={handleFormSubmit}
                                data-test-selector="availabilityForm"
                            >
                                <StyledFieldSet {...styles.fieldset}>
                                    <Typography {...styles.subHeading}>
                                        {translate(
                                            "Enter your details below and we'll get back to you today with an expected delivery date.",
                                        )}
                                    </Typography>

                                    <StyledWrapper {...styles.containerHeading} data-test-selector="addTagsFormHeading">
                                        <StyledWrapper>
                                            <TextField
                                                label={translate("QTY")}
                                                value={qty}
                                                type="number"
                                                min={0}
                                                max={999999}
                                                disabled={!onSuccess || isLoading}
                                                required
                                                error={qtyError}
                                                data-test-selector="product_qtyOrdered"
                                                onKeyPress={keyPressHandler}
                                                onChange={qtyChangeHandler}
                                            />

                                            <TextField
                                                name="Zip Code"
                                                label={translate("Zip code")}
                                                value={zipCode}
                                                required
                                                maxLength={5}
                                                onChange={zipCodeHandler}
                                                error={zipCodeError}
                                                data-test-selector="customerAddressForm_firstName"
                                            />
                                        </StyledWrapper>

                                        <StyledWrapper>
                                            <TextField
                                                name="email"
                                                label={translate("Email")}
                                                value={email}
                                                required
                                                onChange={event => emailChangeHandler(event.target.value)}
                                                error={emailError}
                                                data-test-selector="customerAddressForm_firstName"
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>

                                    <StyledWrapper {...styles.resident}>
                                        <StyledWrapper {...styles.residentLineOne}>
                                            {translate("Residents of AK, HI, and outside the US")}
                                        </StyledWrapper>

                                        <StyledWrapper {...styles.residentLineOne}>
                                            <Typography style={{ fontWeight: "400" }}>
                                                {translate("Please call")}
                                            </Typography>
                                            {translate(" (901) 883-7241 ")}
                                            <Typography style={{ fontWeight: "400" }}>
                                                {translate("for assistance.")}
                                            </Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>

                                    <Button
                                        {...styles.submitButton}
                                        type="submit"
                                        data-test-selector="customerAddressForm_save"
                                    >
                                        {translate("Submit")}
                                    </Button>
                                </StyledFieldSet>
                            </StyledForm>
                        </StyledWrapper>
                    )
                ) : (
                    <>
                        <Typography
                            style={{
                                fontSize: "16px",
                                fontWeight: 400,
                                lineHeight: "24px",
                                color: "#636161",
                            }}
                        >
                            {translate("Thanks for submitting your request.")}
                        </Typography>
                    </>
                )}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(NISProductDetailsAvailability)),
    definition: {
        displayName: "NIS Availability",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
